/**
 * FaraidCalculatorContainer Component
 * Main container for the Faraid (Islamic Inheritance) Calculator
 * 
 * This component manages the state and orchestrates the four main tabs:
 * 1. Senarai Waris (Heir Selection)
 * 2. Carta Waris (Heir Chart Visualisation)
 * 3. Harta Pusaka (Estate Assets)
 * 4. Pengiraan (Calculation Results)
 * 
 * Reference: FaraidInfo.md - K-Faraid MAIS
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { TabNav } from '@components/common/TabNav';
import { SenaraiWarisTab, CartaWarisTab, HartaPusakaTab, PengiraanTab } from './components';
import { calculateFaraid, generateFaraidPdf } from './utils';
import type {
  FaraidTab,
  DeceasedInfo,
  HeirInput,
  HeirType,
  AssetsState,
  AssetType,
  AssetEntry,
  FaraidCalculationResult,
} from './types';
import { HEIRS_BY_CATEGORY } from './types/heirDefinitions';
import { ASSET_TYPES } from './types';
import styles from './FaraidCalculatorContainer.module.css';

/**
 * FaraidCalculatorContainer component props.
 */
interface FaraidCalculatorContainerProps {
  /** Optional additional class name */
  className?: string;
}

/**
 * Initialize empty heirs state
 */
function initializeHeirsState(): Record<HeirType, HeirInput> {
  const heirs: Partial<Record<HeirType, HeirInput>> = {};
  
  const allHeirTypes = [
    ...HEIRS_BY_CATEGORY.waris_utama,
    ...HEIRS_BY_CATEGORY.waris_ganti,
    ...HEIRS_BY_CATEGORY.waris_kedua,
  ];
  
  for (const type of allHeirTypes) {
    heirs[type] = {
      type,
      count: 0,
      isPresent: false,
    };
  }
  
  return heirs as Record<HeirType, HeirInput>;
}

/**
 * Initialize empty assets state
 */
function initializeAssetsState(): AssetsState {
  const assets: AssetEntry[] = ASSET_TYPES.map((type, index) => ({
    id: `asset-${index}`,
    type,
    value: 0,
  }));
  
  return {
    assets,
    totalValue: 0,
  };
}

/**
 * Tab definitions for the calculator
 */
const TABS = [
  { id: 'senarai_waris', label: 'Senarai Waris' },
  { id: 'carta_waris',   label: 'Carta Waris' },
  { id: 'harta_pusaka',  label: 'Harta Pusaka' },
  { id: 'pengiraan',     label: 'Pengiraan' },
];

/**
 * Faraid calculator container component.
 * Manages state and renders the calculator interface.
 */
export const FaraidCalculatorContainer = ({ className = '' }: FaraidCalculatorContainerProps) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<FaraidTab>('senarai_waris');
  
  // Deceased info state
  const [deceased, setDeceased] = useState<DeceasedInfo>({ gender: 'lelaki' });
  
  // Heirs state
  const [heirs, setHeirs] = useState<Record<HeirType, HeirInput>>(initializeHeirsState);
  
  // Assets state
  const [assets, setAssets] = useState<AssetsState>(initializeAssetsState);
  
  // Calculation result state
  const [calculationResult, setCalculationResult] = useState<FaraidCalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Calculate total estate value
  const totalEstateValue = useMemo(() => {
    return assets.assets.reduce((sum, asset) => sum + asset.value, 0);
  }, [assets.assets]);
  
  // Handle tab change
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId as FaraidTab);
    
    // Auto-calculate when switching to Pengiraan tab
    if (tabId === 'pengiraan') {
      performCalculation();
    }
  }, []);
  
  // Handle deceased info change
  const handleDeceasedChange = useCallback((newDeceased: DeceasedInfo) => {
    setDeceased(newDeceased);
    // Clear calculation result when deceased changes
    setCalculationResult(null);
  }, []);
  
  // Handle heir change
  const handleHeirChange = useCallback((type: HeirType, input: Partial<HeirInput>) => {
    setHeirs(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...input,
      },
    }));
    // Clear calculation result when heirs change
    setCalculationResult(null);
  }, []);
  
  // Handle asset value change
  const handleAssetValueChange = useCallback((assetType: AssetType, value: number) => {
    setAssets(prev => {
      const newAssets = prev.assets.map(asset => 
        asset.type === assetType ? { ...asset, value } : asset
      );
      const totalValue = newAssets.reduce((sum, asset) => sum + asset.value, 0);
      
      return {
        assets: newAssets,
        totalValue,
      };
    });
    // Clear calculation result when assets change
    setCalculationResult(null);
  }, []);
  
  // Perform calculation
  const performCalculation = useCallback(() => {
    setIsCalculating(true);
    
    // Use setTimeout to allow UI to update before calculation
    setTimeout(() => {
      const result = calculateFaraid(heirs, deceased, totalEstateValue);
      setCalculationResult(result);
      setIsCalculating(false);
    }, 100);
  }, [heirs, deceased, totalEstateValue]);
  
  // Auto-recalculate when on Pengiraan tab and inputs change
  useEffect(() => {
    if (activeTab === 'pengiraan') {
      performCalculation();
    }
  }, [activeTab, performCalculation]);

  const pdfPreviewResult = useMemo(
    () => calculateFaraid(heirs, deceased, totalEstateValue),
    [heirs, deceased, totalEstateValue]
  );

  const canDownloadPdf =
    pdfPreviewResult.isValid && pdfPreviewResult.shares.length > 0;

  const handleDownloadPdf = useCallback(() => {
    const result = calculateFaraid(heirs, deceased, totalEstateValue);
    if (!result.isValid || result.shares.length === 0) return;
    generateFaraidPdf({ deceased, heirs, assets, result });
  }, [heirs, deceased, totalEstateValue, assets]);
  
  const containerClasses = [styles.container, className].filter(Boolean).join(' ');
  
  return (
    <div className={containerClasses}>
      {/* Tab Navigation */}
      <div className={styles.tabNavWrapper}>
        <TabNav
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className={styles.tabNav}
        />
        <button
          type="button"
          className={styles.downloadPdfButton}
          disabled={!canDownloadPdf}
          onClick={handleDownloadPdf}
        >
          Download report (PDF)
        </button>
      </div>
      
      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'senarai_waris' && (
          <SenaraiWarisTab
            deceased={deceased}
            heirs={heirs}
            onDeceasedChange={handleDeceasedChange}
            onHeirChange={handleHeirChange}
          />
        )}
        
        {activeTab === 'carta_waris' && (
          <CartaWarisTab
            deceased={deceased}
            heirs={heirs}
            onHeirChange={handleHeirChange}
          />
        )}
        
        {activeTab === 'harta_pusaka' && (
          <HartaPusakaTab
            assets={assets}
            onAssetValueChange={handleAssetValueChange}
          />
        )}
        
        {activeTab === 'pengiraan' && (
          <PengiraanTab
            result={calculationResult}
            totalEstate={totalEstateValue}
            isCalculating={isCalculating}
            onRecalculate={performCalculation}
          />
        )}
      </div>
    </div>
  );
};
